/**
 * One support of an element, with the supporting element resolved where one
 * exists. `supportingElementGuid` is null for supports without a target element
 * (e.g. foundations / ground bearings).
 */
export interface ElementSupportLayoutItem {
  relativePosition?: string
  supportingElementGuid: string | null
  supportType: 'point' | 'line'
}

/** Supports grouped by the guid of the element they support. */
export type SupportsByElementIndex = Map<string, ElementSupportLayoutItem[]>

/**
 * Single traversal of the transmission graph: groups every support by the
 * element it supports, resolving the supporting element guid via
 * `support_targets` → `element_targets`. Foundations (supports without a target
 * element) are kept with a null `supportingElementGuid`. Build once per graph
 * snapshot and reuse across the many lookups that share it.
 */
export const buildSupportsByElementIndex = (
  graph: VerticalTransmissionGraph,
): SupportsByElementIndex => {
  const targetGuidBySupportGuid = new Map<string, string>()
  for (const supportTarget of graph.support_targets) {
    // 1-1 in practice; keep the first mapping to match the previous find().
    if (!targetGuidBySupportGuid.has(supportTarget.support_guid)) {
      targetGuidBySupportGuid.set(supportTarget.support_guid, supportTarget.target_guid)
    }
  }

  const elementGuidByTargetGuid = new Map<string, string | null>()
  for (const elementTarget of graph.element_targets) {
    if (!elementGuidByTargetGuid.has(elementTarget.guid)) {
      elementGuidByTargetGuid.set(elementTarget.guid, elementTarget.element_guid ?? null)
    }
  }

  const supportsByElementGuid: SupportsByElementIndex = new Map()
  for (const support of graph.element_supports) {
    if (!support.element_guid) continue

    const targetGuid = targetGuidBySupportGuid.get(support.guid)
    const supportingElementGuid =
      targetGuid != null ? elementGuidByTargetGuid.get(targetGuid) ?? null : null

    const item: ElementSupportLayoutItem = {
      relativePosition: support.relative_position,
      supportingElementGuid,
      supportType: support.support_type,
    }

    const existing = supportsByElementGuid.get(support.element_guid)
    if (existing) existing.push(item)
    else supportsByElementGuid.set(support.element_guid, [item])
  }

  return supportsByElementGuid
}

/**
 * Supports of an element that have a known supporting element (foundations are
 * dropped). Pass a prebuilt `index` to avoid re-traversing the graph per call.
 */
export const getSupportsOfElement = (
  elementGuid: string,
  graph: VerticalTransmissionGraph,
  index?: SupportsByElementIndex,
): TargetGuidAndRelativePosition[] => {
  const resolvedIndex = index ?? buildSupportsByElementIndex(graph)
  return (resolvedIndex.get(elementGuid) ?? [])
    .filter(
      (support): support is ElementSupportLayoutItem & { supportingElementGuid: string } =>
        support.supportingElementGuid !== null,
    )
    .map(support => ({
      relativePosition: support.relativePosition,
      guid: support.supportingElementGuid,
      supportType: support.supportType,
    }))
}
